import { IndividualGoalType } from "./IndividualGoal";
import { ReactNode, useState } from "react";
import { Cookies } from "react-cookie";

import closeIcon from '../images/close-icon.svg'
import attachFileIcon from '../images/attach-file-icon.svg'
import deleteIcon from '../images/delete-icon.svg'
import LinearProgressBar from "./LinearProgressBar";
import SnackbarAlert from "./SnackbarAlert";
import DatePickerInput from "./DatePickerInput";

import '../styles/ImportExcelDialog.css'
import axios from "axios";

interface ImportExcelDialogProps {
    goalIdTarget: number;
    goalList: IndividualGoalType[];
    courseId: number;
    semesterId: number;
    handleClickClose: any;
    isScholarship?: boolean;
}

export default function ImportExcelDialog({ goalIdTarget, goalList, courseId, semesterId, handleClickClose, isScholarship }: ImportExcelDialogProps) {

    const [selectedDate, setSelectedDate] = useState<null | moment.Moment>(null);
    const [loading, setLoading] = useState(false)
    const cookies = new Cookies()

    const goalTarget = goalList.find(goal => goal.id === goalIdTarget)
    const goalNameTarget = goalTarget?.goal || goalTarget?.name

    const [snackbarMessage, setSnackbarMessage] = useState<null | {
        message: ReactNode,
        severity?: 'success' | 'info' | 'warning' | 'error',
        autoHideDuration?: number | null
    }>(null)

    const [excelFile, setExcelFile] = useState<File | null>(null)

    const handleOnFileChange = (e: any) => {
        setSnackbarMessage(null)
        const file = e.target.files[0]
        if (file.size > 25_000_000) {
            setSnackbarMessage({
                message: 'File size must be less than 25MB',
                severity: 'error',
                autoHideDuration: 3000
            })
            return
        }

        if (file.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
            setSnackbarMessage({
                message: 'File type is not supported',
                severity: 'error',
                autoHideDuration: 3000
            })
            return
        } else {
            setExcelFile(file)
        }
    }

    const handleOnFileDelete = () => {
        setExcelFile(null)
        const file = document.querySelector('#upload-excel-file') as HTMLInputElement
        file.value = ''
    }

    const handleClickImport = () => {
        setLoading(true)
        setSnackbarMessage(null)

        const fetchURL = isScholarship ? `ScholarshipProgresses/excel?scholarshipId=${goalIdTarget}` : `IndividualProgresses/excel?individualGoalId=${goalIdTarget}`
        const includeHeaderCheckBox = document.querySelector('#include-headers') as HTMLInputElement
        const includeHeader = includeHeaderCheckBox?.checked || false

        axios.post(fetchURL, {
            Deadline: selectedDate?.format('YYYY-MM-DD'),
            Header: includeHeader,
            Excel: excelFile,
            StaffId: cookies.get('user')?.staffId,
        }, {
            headers: {
                'Content-Type': 'multipart/form-data',
                'Authorization': `Bearer ${cookies.get('user')?.token}`
            }
        }).then(res => {
            setSnackbarMessage({
                message: 'Imported successfully',
                severity: 'success',
                autoHideDuration: 2000
            })
            setTimeout(() => {
                handleClickClose()
                setLoading(false)
            }, 2000)
        }).catch(err => {
            setLoading(false)
            setSnackbarMessage({
                message: (err.response.status === 409 && err.response) ? <><strong>Duplicate student(s) found:</strong><br /> {err.response.data.map((dup: string) => <>{dup}<br /></>)}</> : err.response.data.message || 'Something went wrong',
                severity: 'error',
                autoHideDuration: 3000
            })
        })
    }

    const importTemplate1 = require('../files/Student_ID_Template.xlsx')
    const importTemplate2 = require('../files/Student_ID_Template-with-header.xlsx')

    return (
        <div className="ProgressDialog">
            <div className="EditStudentInfoDialog-dialog">
                <button className="ProgressDialog-close-button" onClick={handleClickClose}>
                    <img src={closeIcon} width={24} height={24} alt="close-icon" />
                </button>
                {
                    (loading) &&
                    <div className="ProgressDialog-linear-progress">
                        <LinearProgressBar />
                    </div>
                }
                <div className="AddStudentDialog-header-container">
                    <h4 className='AddStudentDialog-title'>Import Excel to</h4>
                    <h4 className='AddStudentDialog-goal-name'>{goalNameTarget}</h4>
                </div>
                <div className='AddStudentDialog-deadline'>
                    <DatePickerInput
                        label='Due Date'
                        emptyLabel='Select Date'
                        selectedDate={selectedDate}
                        setSelectedDate={setSelectedDate}
                        disabled={loading}
                        inputVariant="outlined"
                        required
                        style={
                            {
                                padding: '15px 14px',
                            }
                        }
                    />
                </div>
                <div className="ImportExcelDialog-upload-container">
                    <div className={`ImportExcelDialog-upload ${excelFile !== null ? 'active' : ''}`}>
                        <input
                            accept=".xlsx, .xls"
                            className="ImportExcelDialog-upload-input"
                            id="upload-excel-file"
                            type="file"
                            onChange={handleOnFileChange}
                            disabled={loading}
                        />
                        <label htmlFor="upload-excel-file">Upload your excel file here</label>
                        {
                            (excelFile) &&
                            <div className="ProgressDialog-file-item">
                                <img src={attachFileIcon} alt="fileIcon" />
                                <div className="ProgressDialog-file-detail">
                                    <a className="ProgressDialog-file-name" href={URL.createObjectURL(excelFile)} target="_blank" title="Click to download file">{excelFile.name}</a>
                                    <p className="ProgressDialog-file-desc">{Math.round((excelFile.size / 1024 + Number.EPSILON) * 100) / 100} KB</p>
                                </div>
                                <div className='flex gap-dot25'>
                                    <button className='ProgressDialog-delete-file-btn' onClick={handleOnFileDelete} title="Click to delete file" disabled={loading}>
                                        <img src={deleteIcon} alt="deleteIcon" />
                                    </button>
                                </div>
                            </div>
                        }
                    </div>
                </div>
                <div className="ImportExcelDialog-upload-option">
                    <input type="checkbox" id="include-headers" name="have-headers" disabled={loading} />
                    <label htmlFor="include-headers">Include headers in the first row</label>
                </div>
                <div className='example-files'>
                    <p className='desc'>ตัวอย่างไฟล์ Excel</p>
                    <div className='flex gap1'>
                        <a href={importTemplate1} rel="noreferrer">รหัสนิสิต</a>
                        <a href={importTemplate2} rel="noreferrer">รหัสนิสิต (มีหัวตาราง)</a>
                    </div>
                </div>
                <div className="ProgressDialog-bottom-container" >
                    <div className="ProgressDialog-actions" style={{ marginLeft: 'auto' }}>
                        <button className="ProgressDialog-button" onClick={handleClickClose}>Cancel</button>
                        <button className="ProgressDialog-button" onClick={handleClickImport} disabled={selectedDate == null || excelFile == null || loading}>Import</button>
                    </div>
                </div>
            </div>
            {
                (snackbarMessage) &&
                <SnackbarAlert severity={snackbarMessage.severity} message={snackbarMessage.message} />
            }
        </div>
    )
}